import React, { useState } from 'react';
import {
  Box, Typography, Stack, TextField, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Chip, Tooltip,
} from '@mui/material';
import AddIcon            from '@mui/icons-material/Add';
import DeleteIcon         from '@mui/icons-material/Delete';
import EditIcon           from '@mui/icons-material/Edit';
import PersonAddIcon      from '@mui/icons-material/PersonAdd';
import CheckIcon          from '@mui/icons-material/Check';
import CloseIcon          from '@mui/icons-material/Close';
import CalendarMonthIcon  from '@mui/icons-material/CalendarMonth';
import HebrewCalendarPicker from '../components/HebrewCalendarPicker';
import { iconOptions } from '../../../business/NitzavSpotlightWidget/iconOptions';
import {
  SLOT_TYPE_LABELS, SLOT_TYPE_COLORS, HEBREW_MONTH_NAMES, toHebrewYear,
} from '../../../business/ParnasWidget/parnasConstants';

const navy    = '#1e3a5f';
const inputSx = { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' } };

const generateId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const formatSlotLabel = (slot) => {
  if (slot.type === 'daily')   return slot.hDate || slot.date || '';
  if (slot.type === 'weekly')  return slot.parshaName ? `פרשת ${slot.parshaName}` : slot.parshaShabbatDate || '';
  if (slot.type === 'monthly') {
    const monthName = slot.hebrewMonthName || HEBREW_MONTH_NAMES[slot.hebrewMonth] || '';
    return `${monthName} ${toHebrewYear(slot.hebrewYear)}`.trim();
  }
  return '';
};

// ממיר slot קיים לפורמט ה-draftSelected של הלוח
const slotToDraftSelected = (slot) => {
  if (!slot) return null;
  if (slot.type === 'daily')   return { type: 'daily',   date: slot.date, hDate: slot.hDate };
  if (slot.type === 'weekly')  return { type: 'weekly',  parshaName: slot.parshaName, parshaShabbatDate: slot.parshaShabbatDate };
  if (slot.type === 'monthly') return { type: 'monthly', hebrewMonth: slot.hebrewMonth, hebrewYear: slot.hebrewYear, hebrewMonthName: slot.hebrewMonthName };
  return null;
};

/**
 * SponsorsStep
 * ניהול רשימת פרנסים — הוספה / עריכה / מחיקה
 *
 * Props:
 *   formData  { sponsors: [...] }
 *   onChange  (partial) => void
 */
const SponsorsStep = ({ formData, onChange }) => {
  const sponsors = formData?.sponsors || [];

  // ─── Calendar overview dialog ───────────────────────────────────────────
  const [calendarOpen, setCalendarOpen] = useState(false);

  // ─── Dialog state ───────────────────────────────────────────────────────
  const [open,             setOpen]             = useState(false);
  const [editingSponsorId, setEditingSponsorId] = useState(null); // null = new sponsor
  const [editingSlotId,    setEditingSlotId]    = useState(null); // null = new slot
  const [draftName,        setDraftName]        = useState('');
  const [draftSlotType,    setDraftSlotType]    = useState(null);
  const [draftSelected,    setDraftSelected]    = useState(null);
  const [draftNote,        setDraftNote]        = useState('');
  const [draftIcon,        setDraftIcon]        = useState(null);
  const [nameError,        setNameError]        = useState('');
  const [slotError,        setSlotError]        = useState('');

  // ─── Inline name edit ───────────────────────────────────────────────────
  const [editingNameId,  setEditingNameId]  = useState(null);
  const [editingNameVal, setEditingNameVal] = useState('');

  const startNameEdit = (sponsor) => {
    setEditingNameId(sponsor.id);
    setEditingNameVal(sponsor.name);
  };
  const commitNameEdit = () => {
    if (!editingNameVal.trim()) { setEditingNameId(null); return; }
    onChange({
      sponsors: sponsors.map(s =>
        s.id === editingNameId ? { ...s, name: editingNameVal.trim() } : s
      ),
    });
    setEditingNameId(null);
  };
  const cancelNameEdit = () => setEditingNameId(null);

  // ─── All existing slots (for occupation display), excluding slot being edited
  const allSlots = sponsors.flatMap(s => s.slots || []).filter(sl => sl.id !== editingSlotId);

  // ─── Open helpers ────────────────────────────────────────────────────────
  const openNewSponsor = () => {
    setEditingSponsorId(null);
    setEditingSlotId(null);
    setDraftName('');
    setDraftSlotType(null);
    setDraftSelected(null);
    setDraftNote('');
    setDraftIcon(null);
    setNameError('');
    setSlotError('');
    setOpen(true);
  };

  const openAddSlot = (sponsorId) => {
    setEditingSponsorId(sponsorId);
    setEditingSlotId(null);
    setDraftSlotType(null);
    setDraftSelected(null);
    setDraftNote('');
    setDraftIcon(null);
    setNameError('');
    setSlotError('');
    setOpen(true);
  };

  const openEditSlot = (sponsorId, slot) => {
    setEditingSponsorId(sponsorId);
    setEditingSlotId(slot.id);
    setDraftSlotType(slot.type);
    setDraftSelected(slotToDraftSelected(slot));
    setDraftNote(slot.note || '');
    setDraftIcon(slot.icon || null);
    setNameError('');
    setSlotError('');
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingSlotId(null);
  };

  // ─── Confirm ─────────────────────────────────────────────────────────────
  const handleConfirm = () => {
    let valid = true;

    if (!editingSponsorId && !draftName.trim()) {
      setNameError('חובה להזין שם');
      valid = false;
    } else {
      setNameError('');
    }

    if (!draftSelected) {
      setSlotError('יש לבחור מועד מהלוח');
      valid = false;
    } else {
      setSlotError('');
    }

    if (!valid) return;

    const builtSlot = {
      id:   editingSlotId || generateId(),
      type: draftSelected.type,
      ...(draftSelected.type === 'daily'   ? { date: draftSelected.date, hDate: draftSelected.hDate } : {}),
      ...(draftSelected.type === 'weekly'  ? { parshaName: draftSelected.parshaName, parshaShabbatDate: draftSelected.parshaShabbatDate } : {}),
      ...(draftSelected.type === 'monthly' ? { hebrewMonth: draftSelected.hebrewMonth, hebrewYear: draftSelected.hebrewYear, hebrewMonthName: draftSelected.hebrewMonthName } : {}),
      note: draftNote.trim(),
      icon: draftIcon || null,
    };

    if (editingSlotId) {
      // עדכון slot קיים
      onChange({
        sponsors: sponsors.map(s =>
          s.id !== editingSponsorId ? s : {
            ...s,
            slots: (s.slots || []).map(sl => sl.id === editingSlotId ? builtSlot : sl),
          }
        ),
      });
    } else if (editingSponsorId) {
      // הוספת slot לפרנס קיים
      onChange({
        sponsors: sponsors.map(s =>
          s.id === editingSponsorId ? { ...s, slots: [...(s.slots || []), builtSlot] } : s
        ),
      });
    } else {
      // פרנס חדש
      onChange({
        sponsors: [
          ...sponsors,
          { id: generateId(), name: draftName.trim(), slots: [builtSlot] },
        ],
      });
    }

    setOpen(false);
    setEditingSlotId(null);
  };

  // ─── Delete ───────────────────────────────────────────────────────────────
  const deleteSlot = (sponsorId, slotId) => {
    onChange({
      sponsors: sponsors
        .map(s =>
          s.id !== sponsorId ? s : { ...s, slots: (s.slots || []).filter(sl => sl.id !== slotId) }
        )
        .filter(s => (s.slots || []).length > 0),
    });
  };

  const deleteSponsor = (sponsorId) =>
    onChange({ sponsors: sponsors.filter(s => s.id !== sponsorId) });

  // ─── Derived dialog title / button label ─────────────────────────────────
  const isEditingSlot = !!editingSlotId;
  const dialogTitle = isEditingSlot
    ? `ערוך מועד — ${sponsors.find(s => s.id === editingSponsorId)?.name || ''}`
    : editingSponsorId
      ? `הוסף מועד — ${sponsors.find(s => s.id === editingSponsorId)?.name || ''}`
      : 'הוסף פרנס חדש';
  const confirmLabel = isEditingSlot ? 'שמור שינויים' : editingSponsorId ? 'הוסף מועד' : 'הוסף פרנס';

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Box dir="rtl">

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2.5 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, color: navy, fontSize: '1rem', mb: 0.5 }}>
            ניהול פרנסים
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', color: '#6b7280', lineHeight: 1.5 }}>
            הוסף פרנסים ושייך לכל אחד מועד — יומי, שבועי או חודשי.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexShrink={0} sx={{ mr: 1 }}>
          {sponsors.length > 0 && (
            <Button
              size="small"
              variant="outlined"
              endIcon={<CalendarMonthIcon sx={{ fontSize: '1rem !important' }} />}
              onClick={() => setCalendarOpen(true)}
              sx={{
                borderColor: '#d1d5db', color: '#6b7280', fontWeight: 600, fontSize: '0.82rem',
                '&:hover': { bgcolor: '#f9fafb', borderColor: '#9ca3af' },
              }}
            >
              תמונת מצב
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            endIcon={<PersonAddIcon sx={{ fontSize: '1rem !important' }} />}
            onClick={openNewSponsor}
            sx={{
              borderColor: navy, color: navy, fontWeight: 600, fontSize: '0.82rem',
              '&:hover': { bgcolor: '#f0f4f9', borderColor: navy },
            }}
          >
            הוסף פרנס
          </Button>
        </Stack>
      </Box>

      {/* ── Calendar overview dialog ── */}
      <Dialog
        open={calendarOpen}
        onClose={() => setCalendarOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ dir: 'rtl', sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: navy, fontSize: '1rem', pb: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          תמונת מצב — לוח פרנסים
          <IconButton size="small" onClick={() => setCalendarOpen(false)} sx={{ color: '#9ca3af' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <HebrewCalendarPicker
            existingSlots={sponsors.flatMap(s => s.slots || [])}
            sponsors={sponsors}
            readOnly
          />
        </DialogContent>
      </Dialog>

      {/* Empty state */}
      {sponsors.length === 0 ? (
        <Box sx={{
          border: '1.5px dashed #d1d5db', borderRadius: 2, py: 4.5,
          textAlign: 'center', color: '#9ca3af',
        }}>
          <Typography sx={{ fontSize: '0.88rem' }}>עדיין לא נוספו פרנסים</Typography>
          <Typography sx={{ fontSize: '0.78rem', mt: 0.5 }}>לחץ על "הוסף פרנס" כדי להתחיל</Typography>
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {sponsors.map((sponsor) => (
            <Box
              key={sponsor.id}
              sx={{ bgcolor: 'white', border: '1px solid #e5e7eb', borderRadius: 2, p: 2 }}
            >
              {/* Sponsor header row */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                {/* Inline name edit */}
                {editingNameId === sponsor.id ? (
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                    <TextField
                      size="small"
                      value={editingNameVal}
                      onChange={(e) => setEditingNameVal(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') commitNameEdit(); if (e.key === 'Escape') cancelNameEdit(); }}
                      autoFocus
                      sx={{ '& .MuiInputBase-input': { py: '4px', fontSize: '0.875rem' }, ...inputSx }}
                    />
                    <IconButton size="small" onClick={commitNameEdit} sx={{ color: navy }}>
                      <CheckIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton size="small" onClick={cancelNameEdit} sx={{ color: '#9ca3af' }}>
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Stack>
                ) : (
                  <Stack direction="row" alignItems="center" spacing={0.25} sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, color: '#111827', fontSize: '0.925rem' }}>
                      {sponsor.name}
                    </Typography>
                    <Tooltip title="ערוך שם">
                      <IconButton size="small" onClick={() => startNameEdit(sponsor)} sx={{ color: '#d1d5db', '&:hover': { color: navy }, p: '2px' }}>
                        <EditIcon sx={{ fontSize: 13 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                )}

                <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                  <Tooltip title="הוסף מועד לפרנס זה">
                    <IconButton
                      size="small"
                      onClick={() => openAddSlot(sponsor.id)}
                      sx={{ color: '#9ca3af', '&:hover': { color: navy } }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="מחק פרנס">
                    <IconButton
                      size="small"
                      onClick={() => deleteSponsor(sponsor.id)}
                      sx={{ color: '#9ca3af', '&:hover': { color: '#ef4444' } }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Slots */}
              <Stack spacing={0.75}>
                {(sponsor.slots || []).map((slot) => {
                  const SlotIconComp = slot.icon
                    ? iconOptions.find(o => o.id === slot.icon)?.icon
                    : null;
                  return (
                    <Box
                      key={slot.id}
                      sx={{
                        display: 'flex', alignItems: 'flex-start', gap: 1,
                        bgcolor: '#f9fafb', borderRadius: 1.5, px: 1.25, py: 0.75,
                      }}
                    >
                      <Chip
                        label={SLOT_TYPE_LABELS[slot.type]}
                        size="small"
                        sx={{
                          bgcolor: SLOT_TYPE_COLORS[slot.type], color: 'white',
                          fontSize: '0.68rem', height: 20, flexShrink: 0,
                        }}
                      />

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.8rem', color: '#374151', fontWeight: 500 }}>
                          {formatSlotLabel(slot)}
                        </Typography>
                        {slot.note && (
                          <Typography sx={{
                            fontSize: '0.72rem', color: '#6b7280', mt: 0.25,
                            fontStyle: 'italic', lineHeight: 1.3,
                          }}>
                            {slot.note}
                          </Typography>
                        )}
                      </Box>

                      {SlotIconComp && (
                        <SlotIconComp sx={{ fontSize: 15, color: '#9ca3af', flexShrink: 0, mt: 0.25 }} />
                      )}

                      <Tooltip title="ערוך מועד">
                        <IconButton
                          size="small"
                          onClick={() => openEditSlot(sponsor.id, slot)}
                          sx={{ color: '#d1d5db', '&:hover': { color: navy }, p: '2px', flexShrink: 0 }}
                        >
                          <EditIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="הסר מועד">
                        <IconButton
                          size="small"
                          onClick={() => deleteSlot(sponsor.id, slot.id)}
                          sx={{ color: '#d1d5db', '&:hover': { color: '#ef4444' }, p: '2px', flexShrink: 0 }}
                        >
                          <DeleteIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          ))}
        </Stack>
      )}

      {/* ── Dialog ── */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ dir: 'rtl', sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: navy, fontSize: '1rem', pb: 0.5 }}>
          {dialogTitle}
        </DialogTitle>

        <DialogContent sx={{ pt: '12px !important' }}>
          <Stack spacing={2.25}>

            {/* Name — only for new sponsor */}
            {!editingSponsorId && (
              <TextField
                size="small"
                label="שם הפרנס / המשפחה"
                placeholder="משפחת כהן"
                value={draftName}
                onChange={(e) => { setDraftName(e.target.value); setNameError(''); }}
                fullWidth
                autoFocus
                error={!!nameError}
                helperText={nameError || 'השם שיוצג ברכיב'}
                sx={inputSx}
              />
            )}

            {/* Calendar */}
            <Box>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151', mb: 1 }}>
                {isEditingSlot ? 'שנה מועד' : 'בחר מועד'}
              </Typography>
              <HebrewCalendarPicker
                existingSlots={allSlots}
                sponsors={sponsors}
                slotType={draftSlotType}
                onSlotTypeChange={(t) => { setDraftSlotType(t); setDraftSelected(null); setSlotError(''); }}
                onSelect={(sel) => { setDraftSelected(sel); setSlotError(''); }}
                selected={draftSelected}
              />
              {slotError && (
                <Typography sx={{ fontSize: '0.75rem', color: '#ef4444', mt: 0.5 }}>
                  {slotError}
                </Typography>
              )}
              {draftSelected && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1 }}>
                  <Chip
                    label={SLOT_TYPE_LABELS[draftSelected.type]}
                    size="small"
                    sx={{ bgcolor: SLOT_TYPE_COLORS[draftSelected.type], color: 'white', fontSize: '0.72rem' }}
                  />
                  <Typography sx={{ fontSize: '0.82rem', color: '#374151', fontWeight: 500 }}>
                    {formatSlotLabel(draftSelected)}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Note */}
            <TextField
              size="small"
              label="טקסט נוסף (אופציונלי)"
              placeholder="לעילוי נשמת..."
              value={draftNote}
              onChange={(e) => setDraftNote(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              sx={inputSx}
              helperText="הקדשה, הערה, או כל טקסט שיוצג יחד עם השם ברכיב"
            />

            {/* Icon picker */}
            <Box>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151', mb: 0.25 }}>
                אייקון ספציפי למועד זה (אופציונלי)
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', mb: 1 }}>
                אם לא נבחר, ישתמש באייקון הכללי של הרכיב
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {/* Default / none */}
                <Box
                  onClick={() => setDraftIcon(null)}
                  sx={{
                    width: 48, display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    gap: '2px', py: 0.5, borderRadius: '6px', cursor: 'pointer',
                    border: draftIcon === null ? '2px solid #111827' : '1px solid #e5e7eb',
                    bgcolor: draftIcon === null ? '#f9fafb' : 'white',
                    '&:hover': { borderColor: '#9ca3af' },
                  }}
                >
                  <Typography sx={{ fontSize: '0.9rem', color: draftIcon === null ? '#111827' : '#9ca3af' }}>—</Typography>
                  <Typography sx={{ fontSize: '0.52rem', color: draftIcon === null ? '#374151' : '#9ca3af', lineHeight: 1, textAlign: 'center' }}>
                    ברירת מחדל
                  </Typography>
                </Box>

                {iconOptions.map(({ id, label, icon: IconComponent }) => {
                  const isSel = draftIcon === id;
                  return (
                    <Box
                      key={id}
                      onClick={() => setDraftIcon(isSel ? null : id)}
                      sx={{
                        width: 48, display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center',
                        gap: '2px', py: 0.5, borderRadius: '6px', cursor: 'pointer',
                        border: isSel ? '2px solid #111827' : '1px solid #e5e7eb',
                        bgcolor: isSel ? '#f9fafb' : 'white',
                        '&:hover': { borderColor: isSel ? '#111827' : '#9ca3af' },
                      }}
                    >
                      <IconComponent sx={{ fontSize: 18, color: isSel ? '#111827' : '#6b7280' }} />
                      <Typography sx={{
                        fontSize: '0.52rem', lineHeight: 1, textAlign: 'center',
                        color: isSel ? '#111827' : '#9ca3af',
                      }}>
                        {label}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>

          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1, flexDirection: 'row-reverse' }}>
          <Button
            onClick={handleConfirm}
            variant="contained"
            size="small"
            sx={{ bgcolor: navy, fontWeight: 600, '&:hover': { bgcolor: '#152a47' } }}
          >
            {confirmLabel}
          </Button>
          <Button onClick={handleClose} size="small" sx={{ color: '#6b7280' }}>
            ביטול
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SponsorsStep;
